/**
 * Google OAuth return handler — the browser lands here at
 * /dashboard#session_id=XXX after the Emergent auth flow. We exchange that
 * session_id for a cookie on the backend, then navigate to /.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { exchangeSession } from "@/lib/authApi";
import { useAuth } from "@/lib/authContext";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const nav = useNavigate();
  const hasProcessed = useRef(false);
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const session_id = m ? decodeURIComponent(m[1]) : null;

    if (!session_id) {
      nav("/login", { replace: true });
      return;
    }

    exchangeSession(session_id)
      .then(({ user }) => {
        setUser(user);
        // Clear the fragment so a refresh doesn't re-trigger
        window.history.replaceState({}, document.title, "/");
        nav("/", { replace: true, state: { user } });
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Sign-in failed";
        setError(typeof msg === "string" ? msg : "Sign-in failed");
      });
  }, [nav, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      {error ? (
        <div className="max-w-md text-center" data-testid="auth-callback-error">
          <div className="font-serif-display text-3xl text-[#0A0604] mb-3">Access denied</div>
          <div className="text-sm text-[#3A2A10] mb-6">{error}</div>
          <a href="/login" className="inline-block px-5 py-2.5 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm">Return to sign in</a>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-[#3A2A10]" data-testid="auth-callback-loading">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-xs uppercase tracking-[0.25em]">Signing you in…</span>
        </div>
      )}
    </div>
  );
}
